import { ChevronLeft, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { createAnalysis } from '@/client/sdk.gen'
import type {
	AnalysisSourceInput,
	ArtifactResponse,
	SourceTypeEnum,
	SummaryDetailLevel,
} from '@/client/types.gen'

import type { AnalysisLanguage } from '@/components/common/language-switcher'
import { SUPPORTED_ANALYSIS_LANGUAGES } from '@/components/common/language-switcher'
import { Button } from '@/components/ui/button'
import { useDataRefresh, useProductTourOptional } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR, TOUR_MOCK_GENERATION_MARKER } from '@/lib/product-tour/constants'
import { TOUR_STEP } from '@/lib/product-tour/stepIndices'
import { extractErrorMessage, getClientResponseStatus } from '@/utils/error'
import {
	type BpmnGenerationOptionsState,
	DEFAULT_BPMN_GENERATION_OPTIONS,
} from './analysis-modal/bpmnOptions'
import { ContextFilesStep } from './analysis-modal/ContextFilesStep'
import { PrimaryFileStep } from './analysis-modal/PrimaryFileStep'
import { ProgressBar } from './analysis-modal/ProgressBar'
import { ReviewStep } from './analysis-modal/ReviewStep'
import { SummaryOptionsStep } from './analysis-modal/SummaryOptionsStep'
import { TemplateStep } from './analysis-modal/TemplateStep'
import {
	canAddAnalysisSource,
	getTotalSourceCount,
	MAX_ANALYSIS_SOURCES,
} from './analysis-modal/utils'

interface AnalysisModalProps {
	isOpen: boolean
	onClose: () => void
	artifacts: ArtifactResponse[]
	projectId?: string
}

export function AnalysisModal({ isOpen, onClose, artifacts, projectId }: AnalysisModalProps) {
	const { t, currentLanguage } = useTranslation()
	const navigate = useNavigate()
	const { invalidateAnalyses } = useDataRefresh()
	const productTour = useProductTourOptional()
	const [modalStep, setModalStep] = useState(1)
	const [primaryFiles, setPrimaryFiles] = useState<ArtifactResponse[]>([])
	const [contextFiles, setContextFiles] = useState<ArtifactResponse[]>([])
	const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
	const [analysisLanguage, setAnalysisLanguage] = useState<AnalysisLanguage>(() => {
		const normalized = (currentLanguage ?? 'en').split('-')[0] as AnalysisLanguage

		return (SUPPORTED_ANALYSIS_LANGUAGES as readonly string[]).includes(normalized)
			? normalized
			: 'en'
	})
	const [summaryDetailLevel, setSummaryDetailLevel] = useState<SummaryDetailLevel>('standard')
	const [generationUserInstructions, setGenerationUserInstructions] = useState('')
	const [bpmnGenerationOptions, setBpmnGenerationOptions] = useState<BpmnGenerationOptionsState>(
		DEFAULT_BPMN_GENERATION_OPTIONS
	)
	const [isGenerating, setIsGenerating] = useState(false)
	const [modalPortalContainer, setModalPortalContainer] = useState<HTMLElement | null>(null)
	const closeSummarySelectRef = useRef<(() => Promise<void>) | null>(null)

	const closeSummarySelectIfNeeded = useCallback(async () => {
		if (modalStep !== 4 || !closeSummarySelectRef.current) return
		await closeSummarySelectRef.current()
	}, [modalStep])

	const registerCloseSummarySelect = useCallback((close: (() => Promise<void>) | null) => {
		closeSummarySelectRef.current = close
	}, [])

	// Reset selections when the modal opens (not on close — avoids unmount races with Radix Select portals).
	useEffect(() => {
		if (!isOpen) return

		setModalStep(1)
		setPrimaryFiles([])
		setContextFiles([])
		setSelectedTemplate('bpmn')
		setSummaryDetailLevel('standard')
		setGenerationUserInstructions('')
		setBpmnGenerationOptions(DEFAULT_BPMN_GENERATION_OPTIONS)
		setIsGenerating(false)
	}, [isOpen])

	const handlePrimaryContinue = () => {
		if (primaryFiles.length === 0) return
		setModalStep(2)
		if (productTour?.isTourActive && productTour.stepIndex === TOUR_STEP.primaryFile) {
			requestAnimationFrame(() => {
				if (productTour.isTourActive && productTour.stepIndex === TOUR_STEP.primaryFile) {
					productTour.nextStep()
				}
			})
		}
	}

	// Prevent body horizontal scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			const originalOverflow = document.body.style.overflowX
			document.body.style.overflowX = 'hidden'
			return () => {
				document.body.style.overflowX = originalOverflow
			}
		}
	}, [isOpen])

	const handleBack = async () => {
		if (isGenerating) return

		if (modalStep === 1) {
			await closeSummarySelectIfNeeded()
			onClose()
			return
		}

		await closeSummarySelectIfNeeded()
		setModalStep((prevStep) => Math.max(1, prevStep - 1))
	}

	const handleClose = async () => {
		if (isGenerating) return

		await closeSummarySelectIfNeeded()
		onClose()
	}

	const toggleContextFile = (artifact: ArtifactResponse) => {
		const isCurrentlySelected = contextFiles.some((f) => f.id === artifact.id)
		if (isCurrentlySelected) {
			setContextFiles((prev) => prev.filter((f) => f.id !== artifact.id))
			return
		}

		if (!canAddAnalysisSource(primaryFiles, contextFiles)) {
			toast.error(t('pages.projectDetail.modal.errors.maxSources', { max: MAX_ANALYSIS_SOURCES }))
			return
		}

		setContextFiles((prev) => [...prev, artifact])
	}

	const togglePrimaryFile = (artifact: ArtifactResponse) => {
		const isSelected = primaryFiles.some((f) => f.id === artifact.id)
		if (isSelected) {
			setPrimaryFiles((prev) => prev.filter((f) => f.id !== artifact.id))
			return
		}

		if (!canAddAnalysisSource(primaryFiles, contextFiles)) {
			toast.error(t('pages.projectDetail.modal.errors.maxSources', { max: MAX_ANALYSIS_SOURCES }))
			return
		}

		setPrimaryFiles((prev) => [...prev, artifact])
	}

	// Drop context selections that became primary when the user changes step 1
	useEffect(() => {
		const primaryIds = new Set(primaryFiles.map((f) => f.id))
		if (primaryIds.size === 0) return
		setContextFiles((prev) => prev.filter((f) => !primaryIds.has(f.id)))
	}, [primaryFiles])

	const buildSourceInput = (
		artifactId: string,
		sourceType: SourceTypeEnum
	): AnalysisSourceInput => ({
		artifact_id: artifactId,
		source_type: sourceType,
	})

	const handleGenerate = async () => {
		if (isGenerating) return

		if (!selectedTemplate || primaryFiles.length === 0 || !projectId) {
			toast.error(t('pages.projectDetail.modal.errors.missingSelection'))
			return
		}

		if (getTotalSourceCount(primaryFiles, contextFiles) > MAX_ANALYSIS_SOURCES) {
			toast.error(t('pages.projectDetail.modal.errors.maxSources', { max: MAX_ANALYSIS_SOURCES }))
			return
		}

		setIsGenerating(true)

		try {
			const primaryIds = new Set(primaryFiles.map((f) => f.id))
			const contextOnly = contextFiles.filter((f) => !primaryIds.has(f.id))
			const primaryNames = primaryFiles.map((f) => f.name).join(', ')
			const sources: AnalysisSourceInput[] = [
				...primaryFiles.map((file) => buildSourceInput(file.id, 'main')),
				...contextOnly.map((file) => buildSourceInput(file.id, 'context')),
			]

			const trimmedInstructions = generationUserInstructions.trim()
			const summaryUserInstructions = productTour?.isTourActive
				? TOUR_MOCK_GENERATION_MARKER
				: trimmedInstructions || undefined

			// Create analysis via API (background generation starts automatically)
			const response = await createAnalysis({
				path: { project_id: projectId },
				body: {
					name: `BPMN Analysis - ${new Date().toLocaleString()}`,
					description: `Generated from ${primaryNames}${contextOnly.length > 0 ? ` with ${contextOnly.length} context file(s)` : ''}`,
					output_format: 'bpmn',
					language: analysisLanguage,
					sources: sources,
					summary_detail_level: summaryDetailLevel,
					bpmn_generation_options: bpmnGenerationOptions,
					summary_user_instructions: summaryUserInstructions,
				},
			})

			if (response.data) {
				if (productTour?.isTourActive && response.data.id) {
					productTour.setTourAnalysisId(String(response.data.id))
				}
				// Navigate immediately to analysis detail page
				onClose()
				invalidateAnalyses(projectId)
				navigate(`/projects/${projectId}/analyses/${response.data.id}`)
				toast.success(t('pages.projectDetail.modal.success.analysisCreated'))
			} else {
				if (getClientResponseStatus(response) === 409) {
					toast.error(t('pages.projectDetail.analyses.nameDuplicate'))
					return
				}
				throw new Error('Failed to create analysis: No data in response')
			}
		} catch (err: unknown) {
			const errorMessage = extractErrorMessage(
				err,
				t('pages.projectDetail.modal.errors.failedToCreate')
			)
			toast.error(errorMessage)
			console.error('Error creating analysis:', err)
		} finally {
			setIsGenerating(false)
		}
	}

	if (!isOpen) return null

	if (typeof document === 'undefined') return null

	const modalContent = (
		<div
			ref={setModalPortalContainer}
			className="fixed inset-0 flex items-center justify-center bg-black/30 transition-opacity z-50"
			onKeyDown={(e) => {
				if (e.key === 'Escape' && !isGenerating) {
					void handleClose()
				}
			}}
			role="dialog"
			aria-modal="true"
			tabIndex={-1}
		>
			<div
				role="document"
				data-tour={TOUR.analysisModal}
				className="bg-background rounded-2xl ring-1 ring-foreground/10 p-5 max-w-xl w-full mx-4 max-h-[90vh] flex flex-col shadow-lg overflow-x-hidden"
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				<div className="mb-4 flex items-center justify-between gap-2">
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-8 w-8 rounded-full"
						onClick={handleBack}
						disabled={isGenerating}
						aria-label={t('common.previous')}
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>

					<div className="flex-1">
						<ProgressBar currentStep={modalStep} totalSteps={5} />
					</div>

					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-8 w-8 rounded-full"
						onClick={() => void handleClose()}
						disabled={isGenerating}
						aria-label={t('common.close')}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				{modalStep === 1 && (
					<PrimaryFileStep
						artifacts={artifacts}
						selectedFiles={primaryFiles}
						contextFiles={contextFiles}
						onToggle={togglePrimaryFile}
						onContinue={handlePrimaryContinue}
					/>
				)}

				{modalStep === 2 && (
					<ContextFilesStep
						artifacts={artifacts}
						selectedFiles={contextFiles}
						primaryFiles={primaryFiles}
						excludedFromContextIds={primaryFiles.map((f) => f.id)}
						onToggle={toggleContextFile}
						onBack={() => setModalStep(1)}
						onContinue={() => setModalStep(3)}
					/>
				)}

				{modalStep === 3 && (
					<TemplateStep
						selectedTemplate={selectedTemplate}
						onBack={() => setModalStep(2)}
						onContinue={() => setModalStep(4)}
						onTemplateSelect={setSelectedTemplate}
						analysisLanguage={analysisLanguage}
						onLanguageChange={(language) => setAnalysisLanguage(language)}
						bpmnGenerationOptions={bpmnGenerationOptions}
						onBpmnGenerationOptionsChange={setBpmnGenerationOptions}
					/>
				)}

				{modalStep === 4 && (
					<SummaryOptionsStep
						summaryDetailLevel={summaryDetailLevel}
						onSummaryDetailLevelChange={setSummaryDetailLevel}
						generationUserInstructions={generationUserInstructions}
						onGenerationUserInstructionsChange={setGenerationUserInstructions}
						onContinue={() => setModalStep(5)}
						portalContainer={modalPortalContainer}
						registerCloseSelect={registerCloseSummarySelect}
					/>
				)}

				{modalStep === 5 && (
					<ReviewStep
						primaryFiles={primaryFiles}
						contextFiles={contextFiles}
						selectedTemplate={selectedTemplate}
						summaryDetailLevel={summaryDetailLevel}
						generationUserInstructions={generationUserInstructions}
						analysisLanguage={analysisLanguage}
						bpmnGenerationOptions={bpmnGenerationOptions}
						isGenerating={isGenerating}
						onBack={() => setModalStep(4)}
						onGenerate={handleGenerate}
					/>
				)}
			</div>
		</div>
	)

	return createPortal(modalContent, document.body)
}
